import { Trophy, Medal, User, ChevronLeft, ChevronRight, Star, Users, Award, AlertCircle } from "lucide-react"
import { Card } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import BaseLayout from "../components/BaseLayout"
import { useState, useEffect } from "react"
import { useAuth } from "../contexts/AuthContext"
import { motion, AnimatePresence } from "framer-motion"
import AppBar from "../components/AppBar"
import { MAIN_BG_GRADIENT } from '../utils/constants'

const PROFILE_API_URL = process.env.REACT_APP_PLAYOFF_URL;
const REFERRAL_API_URL = process.env.REACT_APP_PROFILE_URL;

// New function to determine and render stars based on rank
function getStars(rank) {
    let numStars = 0;
    if (rank >= 1 && rank <= 25) {
        numStars = 5;
    } else if (rank >= 26 && rank <= 50) {
        numStars = 4;
    } else if (rank >= 51 && rank <= 75) {
        numStars = 3;
    } else if (rank >= 76 && rank <= 100) {
        numStars = 3;
    } else { // For ranks > 100
        numStars = 3;
    }

    const totalStars = 5; // We always show 5 star slots
    const stars = [];
    for (let i = 0; i < totalStars; i++) {
        stars.push(
            <Star
                key={i}
                // Apply yellow color for filled stars, gray for empty
                className={`w-4 h-4 ${i < numStars ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
            />
        );
    }
    return <div className="flex justify-center items-center">{stars}</div>;
}


function getRankIcon(rank) {
    switch (rank) {
        case 1:
            return <Trophy className="w-6 h-6 text-yellow-500" />
        case 2:
            return <Medal className="w-6 h-6 text-gray-400" />
        case 3:
            return <Medal className="w-6 h-6 text-amber-600" />
        default:
            return (
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">#{rank}</span>
                </div>
            )
    }
}

function getRankBadge(rank) {
    if (rank > 3) return null

    const badgeColor =
        rank === 1
            ? "bg-yellow-100 text-yellow-800"
            : rank === 2
                ? "bg-blue-100 text-blue-800"
                : "bg-orange-100 text-orange-800"

    return (
        <Badge variant="secondary" className={`ml-2 ${badgeColor}`}>
            Top {rank}
        </Badge>
    )
}

export default function LeaderboardPage() {
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize] = useState(100)
    const [leaderboardData, setLeaderboardData] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState('referral') // Changed default to 'referral'
    const { currentUser } = useAuth()

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                setIsLoading(true)
                setError(null)
                const apiUrl = activeTab === 'profile' ? PROFILE_API_URL : REFERRAL_API_URL;
                const endpoint =  activeTab === 'profile' ? 'leaderboard' : 'referral-leaderboard';
                
                const headers = {};
                if (activeTab === 'referral' && currentUser) {
                    const token = await currentUser.getIdToken();
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const response = await fetch(`${apiUrl}/${endpoint}?page=${currentPage}&page_size=${pageSize}`, {
                    headers
                })
                if (!response.ok) {
                    throw new Error('Failed to fetch leaderboard data')
                }
                const data = await response.json()
                setLeaderboardData(data.data)
                setTotalCount(data.pagination.total_count)
                setTotalPages(data.pagination.total_pages)
            } catch (err) {
                setError(err.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (activeTab === 'referral' && !currentUser) {
            setError('Please sign in to view the referral leaderboard')
            return
        }

        fetchLeaderboard()
    }, [currentPage, pageSize, activeTab, currentUser])

    const handlePreviousPage = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 1))
    }

    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
    }

    return (
        <div className={`relative min-h-screen overflow-hidden ${MAIN_BG_GRADIENT}`}>
            {/* Mouse glow effect */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div id="mouse-glow" className="absolute w-72 h-72 bg-white/30 rounded-full blur-3xl -z-10 transition-transform"></div>
            </div>

            {/* AppBar */}
            <AppBar 
                showUpgradeButton={true}
                userData={null}
            />

            <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
                {/* Header with Tabs */}
                <motion.div 
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-bold text-gray-900 mb-6">Leaderboard</h1>
                    
                    {/* Tab Navigation */}
                    <div className="bg-white/60 backdrop-blur-md rounded-xl p-1 border border-gray-200/50">
                        <div className="flex space-x-2">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                    setActiveTab('referral')
                                    setCurrentPage(1)
                                }}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    activeTab === 'referral'
                                        ? 'bg-black text-white shadow-md'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                                }`}
                            >
                                <div className="flex items-center justify-center space-x-2">
                                    <Users className="w-4 h-4" />
                                    <span>Referral Leaderboard</span>
                                </div>
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                    setActiveTab('profile')
                                    setCurrentPage(1)
                                }}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    activeTab === 'profile'
                                        ? 'bg-black text-white shadow-md'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                                }`}
                            >
                                <div className="flex items-center justify-center space-x-2">
                                    <Award className="w-4 h-4" />
                                    <span>Profile Face-Off</span>
                                </div>
                            </motion.button>
                        </div>
                    </div>
                </motion.div>

                {/* Error Message */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-xl"
                        >
                            <div className="flex items-center text-red-700">
                                <AlertCircle className="w-5 h-5 mr-2" />
                                <span className="font-medium">Error loading leaderboard data</span>
                            </div>
                            <p className="mt-1 text-sm text-red-600">{error}</p>
                            <motion.button 
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                    setError(null)
                                    setCurrentPage(1)
                                }}
                                className="mt-2 text-sm text-red-700 hover:text-red-800 font-medium"
                            >
                                Try again
                            </motion.button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Column Headers */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider bg-white/40 backdrop-blur-sm rounded-lg mb-2">
                    <div className="col-span-1">Rank</div>
                    <div className="col-span-6">User</div>
                    <div className="col-span-3 text-center">Stars</div>
                    <div className="col-span-2 text-right">
                        {activeTab === 'profile' ? 'Score' : 'Referrals'}
                    </div>
                </div>

                {/* Leaderboard Entries */}
                <div className="space-y-2">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-center"
                            >
                                <motion.img
                                    src="/logo.png"
                                    alt="Quis Lab Logo"
                                    className="h-16 w-16 mx-auto mb-4"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                />
                                <p className="text-gray-600">Loading leaderboard...</p>
                            </motion.div>
                        </div>
                    ) : (
                        <AnimatePresence mode="wait">
                            {leaderboardData.map((entry, index) => {
                                const rank = (currentPage - 1) * pageSize + index + 1
                                return (
                                    <motion.div
                                        key={entry.name}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.2, delay: index * 0.05 }}
                                    >
                                        <Card className="p-4 hover:shadow-lg transition-all duration-200 bg-white/60 backdrop-blur-sm border border-gray-200/50">
                                            <div className="grid grid-cols-12 gap-4 items-center">
                                                {/* Rank Icon */}
                                                <div className="col-span-1 flex justify-center">
                                                    {rank <= 3 ? (
                                                        <motion.div
                                                            whileHover={{ scale: 1.1 }}
                                                            className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-50 flex items-center justify-center shadow-md"
                                                        >
                                                            {getRankIcon(rank)}
                                                        </motion.div>
                                                    ) : (
                                                        <motion.div
                                                            whileHover={{ scale: 1.1 }}
                                                            className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-sm"
                                                        >
                                                            {getRankIcon(rank)}
                                                        </motion.div>
                                                    )}
                                                </div>

                                                {/* Profile and Info */}
                                                <div className="col-span-6 flex items-center space-x-3">
                                                    {/* Avatar */}
                                                    <motion.div
                                                        whileHover={{ scale: 1.05 }}
                                                        className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-sm"
                                                    >
                                                        <User className="w-5 h-5 text-gray-900" />
                                                    </motion.div>

                                                    {/* Name and Description */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center">
                                                            <motion.a
                                                                whileHover={{ scale: 1.02 }}
                                                                href={entry.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-base font-semibold text-gray-900 truncate hover:text-gray-600 transition-colors"
                                                            >
                                                                {entry.name}
                                                            </motion.a>
                                                            {getRankBadge(rank)}
                                                        </div>
                                                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">
                                                            {activeTab === 'profile' 
                                                                ? (entry.current_company?.title && entry.current_company?.name 
                                                                    ? `${entry.current_company.title} @ ${entry.current_company.name}` 
                                                                    : entry.position || 'No company specified')
                                                                : `Score: ${entry.score} | Referral Code: ${entry.referral_code || 'N/A'}`}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Stars Column */}
                                                <div className="col-span-3 flex justify-center">
                                                    {getStars(rank)}
                                                </div>

                                                {/* Score/Referrals */}
                                                <div className="col-span-2 text-right">
                                                    <div className="text-xl font-bold text-gray-900">
                                                        {activeTab === 'profile' ? entry.score : entry.referral_count}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {activeTab === 'profile' ? 'Score' : 'Referrals'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </motion.div>
                                )
                            })}
                        </AnimatePresence>
                    )}
                </div>

                {/* Pagination */}
                <div className="mt-6 flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-gray-200/50">
                    <div className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
                    </div>
                    <div className="flex items-center space-x-3">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1 || isLoading}
                            className={`p-2 rounded-lg ${
                                currentPage === 1 || isLoading
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-600 hover:bg-white/50'
                            }`}
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </motion.button>
                        <span className="text-sm font-medium text-gray-700">
                            Page {currentPage} of {totalPages}
                        </span>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages || isLoading}
                            className={`p-2 rounded-lg ${
                                currentPage === totalPages || isLoading
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-600 hover:bg-white/50'
                            }`}
                        >
                            <ChevronRight className="w-5 h-5" />
                        </motion.button>
                    </div>
                </div>
            </div>
        </div>
    )
}